"use client"

import { useParams } from 'next/navigation'
import { useQuery } from '@tanstack/react-query'
import { apiGetAllNews } from '@/services/newsHome/api'

interface PartnersSectionProps {
    companyId?: string | number;
}

export default function PartnersSection({ companyId }: PartnersSectionProps) {
    const params = useParams()

    const finalId = companyId ? companyId : (params?.id || 0);
    const newsTypeId = Number(finalId);

    const { data } = useQuery({
        // Khóa cache cho truy vấn logo đối tác theo loại tin
        queryKey: ['partners-logos', newsTypeId],
        queryFn: async () => {
            const res = await apiGetAllNews({
                keywords: '',
                newsTypeId: newsTypeId,
                isPublic: 'true',
                page: 1,
                pageSize: 100
            })
            return res?.isOK ? res.result.items : []
        },
        staleTime: 5 * 60 * 1000,
        enabled: !!newsTypeId,
    })

    // Lọc bài viết chứa nội dung logo đối tác
    const partnerItem = data?.find((item: any) => item.seoDescription === 'Logo đối tác')

    // Tách tiêu đề và mô tả phụ từ nội dung mô tả
    const descriptionParts = (partnerItem?.description || "").split('\n')
    const title = descriptionParts[0]
    const subTitle = descriptionParts[1]

    // Trích xuất danh sách logo từ nội dung HTML
    const rawLogos = (() => {
        if (!partnerItem) return []

        const contentHtml = partnerItem.content || ""

        const regex = /<img[^>]+src="([^">]+)"/g
        const extractedImages: string[] = []
        let match

        while ((match = regex.exec(contentHtml)) !== null) {
            if (match[1]) {
                extractedImages.push(match[1])
            }
        }

        return extractedImages
    })()

    // Nhân đôi danh sách logo để tạo hiệu ứng chạy liên tục
    const LOGOS = rawLogos.length > 0
        ? [...rawLogos, ...rawLogos]
        : []

    // Điều kiện ẩn section khi không có logo đối tác
    if (LOGOS.length === 0) return null

    return (
        <section className="py-10 md:py-12 bg-white dark:bg-white overflow-hidden transition-colors duration-300">
            <div className="max-w-7xl mx-auto px-6 text-center">
                <h2
                    className="text-2xl md:text-3xl mb-4"
                    style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        color: "#2D3746",
                        fontWeight: 600
                    }}
                >
                    {title}
                </h2>
                <p
                    className="text-sm md:text-base max-w-3xl mx-auto mb-8 md:mb-10 leading-relaxed"
                    style={{
                        fontFamily: "'Helvetica Neue', Arial, sans-serif",
                        color: "#2D3746",
                        fontSize: "13px"
                    }}
                >
                    {subTitle}
                </p>

                <div className="relative">
                    <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 md:w-48 bg-gradient-to-r from-white dark:from-white to-transparent z-10" />
                    <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 md:w-48 bg-gradient-to-l from-white dark:from-white to-transparent z-10" />

                    <div className="overflow-hidden h-12 md:h-14 flex items-center">
                        <div className="flex animate-marquee-fast whitespace-nowrap">
                            {LOGOS.map((src, index) => (
                                <div
                                    key={`logo-${index}`}
                                    className="mx-6 md:mx-10 flex-shrink-0 w-[120px] flex items-center justify-center"
                                >
                                    <img
                                        src={src}
                                        alt={`Đối tác ${index}`}
                                        width={120}
                                        height={60}
                                        className="h-9 md:h-10 w-auto max-w-full object-contain transition-all duration-300"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Khối định nghĩa animation marquee cho danh sách logo */}
            <style jsx>{`
                @keyframes marquee-fast {
                    from { transform: translateX(0); }
                    to { transform: translateX(-50%); }
                }
                .animate-marquee-fast {
                    animation: marquee-fast 18s linear infinite;
                    will-change: transform;
                }
                @media (min-width: 768px) {
                    .animate-marquee-fast { animation-duration: 80s; }
                }
            `}</style>
        </section>
    )
}